import { z } from 'zod'

import { Language } from '@nxt-insurance/constants'

import { CALENDLY_STAGING_LINK } from '@flows/configs/shared/mgaLight/constants'
import type { AnyStepConfiguration } from '@flows/core/types/stepTypes'
import { SubFlow } from '@flows/insurance/types/subFlowTypes'

import type { ChildSavingStepParams } from './types'

const getCalendlyLink = (params: ChildSavingStepParams) => {
  if (params.environment === 'production') {
    return params.getCurrentLanguage() === Language.De
      ? 'https://calendly.com/getsafe-leben-gesundheit/av-kinder'
      : 'https://calendly.com/getsafe-leben-gesundheit/pension-children'
  }

  return CALENDLY_STAGING_LINK
}

export const progressLastStep = (params: ChildSavingStepParams) => {
  return {
    schema: z.object({}),
    stepContent: [
      {
        type: 'harmonyTitle',
        text: {
          type: 'translatableText',
          translationKey: 'sharedFlows_mgaCommon:steps.progress_last_step.title',
        },
      },
      {
        type: 'cardHighlightsRow',
        highlights: [
          {
            title: {
              type: 'translatableText',
              translationKey: 'sharedFlows_mgaCommon:steps.progress_last_step.highlight1.title',
            },
            description: {
              type: 'translatableText',
              translationKey:
                'sharedFlows_mgaCommon:steps.progress_last_step.highlight1.description',
            },
            infoContent: {
              title: {
                type: 'translatableText',
                translationKey:
                  'sharedFlows_mgaCommon:steps.progress_last_step.highlight1.infoContent.title',
              },
              description: {
                type: 'translatableText',
                translationKey:
                  'sharedFlows_mgaCommon:steps.progress_last_step.highlight1.infoContent.description',
              },
              imageSrc:
                'https://cdn.sanity.io/images/p4gom3ch/production/a681ca3fe7b458402c29c07f9643cbe3b2366311-981x441.png',
            },
          },
          {
            title: {
              type: 'translatableText',
              translationKey: 'sharedFlows_mgaCommon:steps.progress_last_step.highlight2.title',
            },
            description: {
              type: 'translatableText',
              translationKey:
                'sharedFlows_mgaCommon:steps.progress_last_step.highlight2.description',
            },
            infoContent: {
              title: {
                type: 'translatableText',
                translationKey:
                  'sharedFlows_mgaCommon:steps.progress_last_step.highlight2.infoContent.title',
              },
              description: {
                type: 'translatableText',
                translationKey:
                  'sharedFlows_mgaCommon:steps.progress_last_step.highlight2.infoContent.description',
              },
              imageSrc:
                'https://cdn.sanity.io/images/p4gom3ch/production/b01f19b3148417182809ed8317c7cac5dafdd061-981x441.png',
            },
          },
          {
            title: {
              type: 'translatableText',
              translationKey: 'sharedFlows_mgaCommon:steps.progress_last_step.highlight3.title',
            },
            description: {
              type: 'translatableText',
              translationKey:
                'sharedFlows_mgaCommon:steps.progress_last_step.highlight3.description',
            },
            infoContent: {
              title: {
                type: 'translatableText',
                translationKey:
                  'sharedFlows_mgaCommon:steps.progress_last_step.highlight3.infoContent.title',
              },
              description: {
                type: 'translatableText',
                translationKey:
                  'sharedFlows_mgaCommon:steps.progress_last_step.highlight3.infoContent.description',
              },
              imageSrc:
                'https://cdn.sanity.io/images/p4gom3ch/production/2d81eec4658ecf96a76c04ccfaaa625c1c49317b-981x441.png',
            },
          },
        ],
      },
      {
        type: 'stepper',
        steps: [
          {
            status: 'completed',
            title: {
              type: 'translatableText',
              translationKey: 'sharedFlows_mgaCommon:steps.progress.stepper.item1.title',
            },
            description: {
              type: 'translatableText',
              translationKey: 'sharedFlows_mgaCommon:steps.progress.stepper.item1.description',
            },
          },
          {
            status: 'active',
            title: {
              type: 'translatableText',
              translationKey: 'sharedFlows_mgaCommon:steps.progress.stepper.item2.title',
            },
            description: {
              type: 'translatableText',
              translationKey: 'sharedFlows_mgaCommon:steps.progress.stepper.item2.description',
            },
          },
          {
            status: 'pending',
            title: {
              type: 'translatableText',
              translationKey: 'sharedFlows_mgaCommon:steps.progress.stepper.item3.title',
            },
            description: {
              type: 'translatableText',
              translationKey: 'sharedFlows_mgaCommon:steps.progress.stepper.item3.description',
            },
          },
          {
            status: 'pending',
            title: {
              type: 'translatableText',
              translationKey: 'sharedFlows_mgaCommon:steps.progress.stepper.item4.title',
            },
            description: {
              type: 'translatableText',
              translationKey: 'sharedFlows_mgaCommon:steps.progress.stepper.item4.description',
            },
          },
        ],
      },
      {
        type: 'calendly',
        title: {
          type: 'translatableText',
          translationKey: 'sharedFlows_common:generic.buttons.continue',
        },
        link: getCalendlyLink(params),
      },
    ],
    skipStep: {
      condition: () => params.platform === 'web',
      targetStepName: 'success',
    },
    onPrepare: {
      type: 'dispatchedHook',
      callbackConfig: {
        name: 'updateAndCompleteConsultationRequest',
        inputs: [],
        outputs: [],
      },
    },
    onSubmit: {
      type: 'dispatchedHook',
      callbackConfig: {
        name: 'updateConsultationRequest',
        inputs: [],
        outputs: [],
      },
    },
    hideBackButton: true,
    hideFooter: true,
    nextStep: 'appointment_details',
    subFlow: SubFlow.Common,
  } as const satisfies AnyStepConfiguration
}
